import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Heart, ArrowRight, XCircle } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../lib/cart";

export function PaymentSuccess() {
    const [sp] = useSearchParams();
    const sessionId = sp.get("session_id");
    const [status, setStatus] = useState({ payment_status: "pending", status: "pending" });
    const [info, setInfo] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const { clear } = useCart();

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        const poll = async (n = 0) => {
            try {
                const { data } = await api.get(`/checkout/status/${sessionId}`);
                if (cancelled) return;
                setStatus(data); setInfo(data); setAttempts(n);
                if (data.payment_status === "paid") {
                    clear();
                    return;
                }
                if (data.status === "expired") return;
                if (n < 10) setTimeout(() => poll(n + 1), 2000);
            } catch {
                if (n < 5) setTimeout(() => poll(n + 1), 2500);
            }
        };
        poll(0);
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    const paid = status.payment_status === "paid";
    const expired = status.status === "expired";
    const isDonation = info?.metadata?.purpose === "donation";
    return <ResultCard paid={paid} expired={expired} isDonation={isDonation} amount={info?.amount_total ? info.amount_total / 100 : null} attempts={attempts} />;
}

export function PayPalReturn() {
    const [sp] = useSearchParams();
    const token = sp.get("token"); // PayPal puts the order id in "token"
    const [state, setState] = useState({ phase: "capturing" });
    const { clear } = useCart();

    useEffect(() => {
        if (!token) {
            setState({ phase: "error", message: "Missing PayPal order id." });
            return;
        }
        (async () => {
            try {
                const { data } = await api.post(`/paypal/capture/${token}`);
                if (data.status === "COMPLETED") {
                    clear();
                    setState({ phase: "paid", isDonation: data.purpose === "donation", amount: data.amount });
                } else {
                    setState({ phase: "error", message: `PayPal status: ${data.status}` });
                }
            } catch (e) {
                setState({ phase: "error", message: "We couldn't confirm your PayPal payment. Please contact us if you were charged." });
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (state.phase === "capturing") {
        return (
            <div className="min-h-[70vh] flex items-center justify-center dawn-wash p-6" data-testid="paypal-return-page">
                <div className="faith-card p-10 max-w-lg w-full text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-[var(--brand-coral)] mx-auto mb-5" />
                    <h1 className="font-serif text-2xl text-[var(--brand-navy)] mb-2">Finalizing PayPal payment...</h1>
                </div>
            </div>
        );
    }
    if (state.phase === "paid") {
        return <ResultCard paid={true} expired={false} isDonation={state.isDonation} amount={state.amount} attempts={0} />;
    }
    return (
        <div className="min-h-[70vh] flex items-center justify-center dawn-wash p-6">
            <div className="faith-card p-10 max-w-lg w-full text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 text-red-700 flex items-center justify-center mx-auto mb-5"><XCircle className="w-8 h-8" /></div>
                <h1 className="font-serif text-3xl text-[var(--brand-navy)] mb-3">Payment not completed</h1>
                <p className="text-slate-600 mb-6">{state.message}</p>
                <Link to="/" className="btn-gold">Return Home</Link>
            </div>
        </div>
    );
}

function ResultCard({ paid, expired, isDonation, amount, attempts }) {
    return (
        <div className="min-h-[70vh] flex items-center justify-center dawn-wash p-6" data-testid="payment-success-page">
            <div className="faith-card p-10 max-w-lg w-full text-center">
                {paid ? (
                    <>
                        <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-5">
                            <CheckCircle2 className="w-8 h-8" />
                        </div>
                        <h1 className="font-serif text-3xl text-[var(--brand-navy)] mb-3">
                            {isDonation ? "Thank you for your gift!" : "Payment received."}
                        </h1>
                        <p className="text-slate-600 mb-2">
                            {isDonation
                                ? "Your generosity is sowing seeds of restoration for veterans in need. May YHWH bless you."
                                : "Your order is being prepared. A receipt has been sent to your email."}
                        </p>
                        {amount && (
                            <div className="text-2xl font-serif text-[var(--brand-navy)] mt-4">
                                ${Number(amount).toFixed(2)}
                            </div>
                        )}
                        <Link to={isDonation ? "/workforce" : "/services"} className="btn-gold mt-8" data-testid="success-continue-btn">
                            {isDonation ? <><Heart className="w-4 h-4 mr-2" /> Back to Mission</> : <>Back to Services <ArrowRight className="w-4 h-4 ml-2" /></>}
                        </Link>
                    </>
                ) : expired ? (
                    <>
                        <div className="w-16 h-16 rounded-full bg-red-100 text-red-700 flex items-center justify-center mx-auto mb-5">
                            <XCircle className="w-8 h-8" />
                        </div>
                        <h1 className="font-serif text-3xl text-[var(--brand-navy)] mb-3">Session expired</h1>
                        <p className="text-slate-600 mb-6">Your checkout session expired. Please try again.</p>
                        <Link to="/workforce" className="btn-gold">Return Home</Link>
                    </>
                ) : (
                    <>
                        <Loader2 className="w-10 h-10 animate-spin text-[var(--brand-coral)] mx-auto mb-5" />
                        <h1 className="font-serif text-2xl text-[var(--brand-navy)] mb-2">Confirming your payment...</h1>
                        <p className="text-sm text-slate-500">Attempt {attempts + 1} of 10</p>
                    </>
                )}
            </div>
        </div>
    );
}

export function PaymentCancel() {
    return (
        <div className="min-h-[70vh] flex items-center justify-center dawn-wash p-6" data-testid="payment-cancel-page">
            <div className="faith-card p-10 max-w-lg w-full text-center">
                <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto mb-5">
                    <XCircle className="w-8 h-8" />
                </div>
                <h1 className="font-serif text-3xl text-[var(--brand-navy)] mb-3">Payment cancelled</h1>
                <p className="text-slate-600 mb-6">No charge was made. You can try again any time.</p>
                <div className="flex gap-3 justify-center">
                    <Link to="/" className="btn-outline-navy">Back to Home</Link>
                    <Link to="/workforce" className="btn-gold">Support the Mission</Link>
                </div>
            </div>
        </div>
    );
}
